import type {
	BaseItemSchema,
	PartTestFormatIntroItem,
	SessionDuration,
} from "@memorang/types";
import type { TrackAnswerEventRequest } from "../api/types";
import { isPartTestFormatIntroItem } from "./intro-screens";

// Section durations live on the part's PART_TEST_FORMAT intro screen
// (content.sectionsTable, in minutes). Sessions with a single part intro
// (e.g. diagnostics) omit content.partNumber, so the lone intro applies.
export const getSectionDurationSeconds = (
	sessionItems: BaseItemSchema[][] | undefined,
	partNumber: number,
	sectionNumber?: number | string,
): number => {
	const partIntros: PartTestFormatIntroItem[] = [];
	for (const block of sessionItems ?? []) {
		for (const item of block) {
			if (isPartTestFormatIntroItem(item)) {
				partIntros.push(item);
			}
		}
	}

	const intro =
		partIntros.find((item) => item.content.partNumber === partNumber) ??
		(partIntros.length === 1 ? partIntros[0] : undefined);
	if (!intro) {
		return 0;
	}

	const section = intro.content.sectionsTable?.find(
		(entry) => entry.section === Number(sectionNumber),
	);
	const durationMinutes =
		section?.durationMinutes ?? intro.content.durationMinutes;
	return durationMinutes ? durationMinutes * 60 : 0;
};

export const getSectionElapsedKey = (
	partNumber: number,
	sectionNumber: number | string,
): string => `${partNumber}-${sectionNumber}`;

export const parseSectionElapsedFromDurations = (
	durations: SessionDuration[] | undefined,
): Record<string, number> => {
	const sectionElapsedSeconds: Record<string, number> = {};
	for (const partDuration of durations ?? []) {
		for (const sectionDuration of partDuration.sections) {
			if (typeof sectionDuration.timeElapsed === "number") {
				sectionElapsedSeconds[
					getSectionElapsedKey(partDuration.part, sectionDuration.section)
				] = sectionDuration.timeElapsed;
			}
		}
	}
	return sectionElapsedSeconds;
};

export const buildSectionDurationsMetadata = (
	sectionElapsedSeconds: Record<string, number>,
): TrackAnswerEventRequest["metadata"] => {
	const sectionsByPart = new Map<
		number,
		{ section: number; timeElapsed: number }[]
	>();
	for (const [key, timeElapsed] of Object.entries(sectionElapsedSeconds)) {
		const [partNumber, sectionNumber] = key.split("-").map(Number);
		const sections = sectionsByPart.get(partNumber) ?? [];
		sections.push({ section: sectionNumber, timeElapsed });
		sectionsByPart.set(partNumber, sections);
	}
	if (sectionsByPart.size === 0) {
		return undefined;
	}
	return {
		durations: [...sectionsByPart.entries()].map(([part, sections]) => ({
			part,
			sections,
		})),
	};
};
